<template>
  <FlashNotifications ref="flash" />

  <!-- Desktop -->
  <div class="hidden sm:flex sm:flex-col bg-gray-50 dark:bg-[#0d1117]" style="height: calc(100vh - 57px)">

    <!-- Header -->
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5 gap-4">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white leading-none">User Invitations</h1>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            {{ page.total_records ?? 0 }} total {{ page.total_records === 1 ? 'invitation' : 'invitations' }}
          </p>
        </div>
        <div class="flex items-center gap-2.5">
          <!-- Sort -->
          <select v-model="sortBy" class="text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="created_at">Date invited</option>
            <option value="responded_at">Date responded</option>
            <option value="status">Status</option>
            <option value="invited_by_admin_id">Invited by</option>
          </select>
          <button @click="toggleSortOrder"
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <svg class="w-3.5 h-3.5 transition-transform" :class="sortOrder === 'ASC' ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
            {{ sortOrder === 'DESC' ? 'Newest' : 'Oldest' }}
          </button>
          <!-- Search -->
          <div class="relative">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
            <input v-model="search" @input="onSearchInput" placeholder="Search name or email…"
              class="pl-9 pr-4 py-2 w-60 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
          </div>
          <!-- Send Invite -->
          <button @click="openModal"
            class="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            Send Invite
          </button>
        </div>
      </div>
    </div>

    <!-- Table area -->
    <div class="flex-1 overflow-y-auto">
      <!-- Skeleton -->
      <div v-if="loading" class="animate-pulse p-6 space-y-3">
        <div class="h-10 bg-gray-100 dark:bg-gray-800 rounded-xl w-full"></div>
        <div v-for="n in 8" :key="n" class="flex items-center gap-4 px-4 py-3 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
          <div class="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
          <div class="flex-1 space-y-1.5">
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
            <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-56"></div>
          </div>
          <div class="h-5 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          <div class="h-3 w-24 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
      </div>

      <!-- Table -->
      <div v-else class="p-6">
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-100 dark:border-gray-700">
                <th class="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Invitee</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden lg:table-cell">Invited by</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden xl:table-cell">Date invited</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden xl:table-cell">Responded</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden lg:table-cell">Email opened</th>
                <th class="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50 dark:divide-gray-700/60">
              <tr v-for="inv in invitations" :key="inv.id" class="hover:bg-gray-50/60 dark:hover:bg-gray-700/20 transition-colors">

                <!-- Invitee -->
                <td class="px-5 py-3.5">
                  <div class="flex items-center gap-3">
                    <div class="flex-shrink-0">
                      <img v-if="inv.joined_user?.profile_picture_url"
                        :src="inv.joined_user.profile_picture_url"
                        class="w-8 h-8 rounded-full object-cover" />
                      <div v-else
                        class="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold"
                        :style="avatarStyle(inv.name)">
                        {{ inv.avatar_name }}
                      </div>
                    </div>
                    <div class="min-w-0">
                      <p class="font-medium text-gray-800 dark:text-gray-100 truncate max-w-[160px]">{{ inv.name }}</p>
                      <p class="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[160px]">{{ inv.email }}</p>
                    </div>
                  </div>
                </td>

                <!-- Status -->
                <td class="px-4 py-3.5">
                  <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1"
                    :class="{
                      'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400': inv.status === 'pending',
                      'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400': inv.status === 'joined',
                      'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400': inv.status === 'declined',
                    }">
                    <span class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="{
                        'bg-blue-500': inv.status === 'pending',
                        'bg-emerald-500': inv.status === 'joined',
                        'bg-red-500': inv.status === 'declined',
                      }"></span>
                    {{ inv.status.charAt(0).toUpperCase() + inv.status.slice(1) }}
                  </span>
                </td>

                <!-- Invited by -->
                <td class="px-4 py-3.5 hidden lg:table-cell">
                  <div class="flex items-center gap-2" v-if="inv.host">
                    <img v-if="inv.host.profile_picture_url"
                      :src="inv.host.profile_picture_url"
                      class="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                    <div v-else
                      class="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                      :style="avatarStyle(inv.host.full_name)">
                      {{ inv.host.avatar_name }}
                    </div>
                    <span class="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[120px]">{{ inv.host.full_name }}</span>
                  </div>
                  <span v-else class="text-xs text-gray-400">—</span>
                </td>

                <!-- Date invited -->
                <td class="px-4 py-3.5 hidden xl:table-cell">
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(inv.created_at) }}</span>
                </td>

                <!-- Responded -->
                <td class="px-4 py-3.5 hidden xl:table-cell">
                  <span v-if="inv.responded_at" class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(inv.responded_at) }}</span>
                  <span v-else class="text-xs text-gray-300 dark:text-gray-600">—</span>
                </td>

                <!-- Email opened -->
                <td class="px-4 py-3.5 hidden lg:table-cell">
                  <span v-if="inv.is_email_opened"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                    </svg>
                    Opened
                  </span>
                  <span v-else class="text-[11px] text-gray-400 dark:text-gray-500">Not opened</span>
                </td>

                <!-- Actions -->
                <td class="px-4 py-3.5 text-right">
                  <button v-if="inv.status !== 'joined'"
                    @click="reInvite(inv.id)"
                    :disabled="reinviting === inv.id"
                    class="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-40 transition">
                    {{ reinviting === inv.id ? 'Sending…' : 'Re-invite' }}
                  </button>
                </td>
              </tr>

              <!-- Empty -->
              <tr v-if="!invitations.length">
                <td colspan="7">
                  <div class="flex flex-col items-center justify-center py-20 text-center">
                    <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                      <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 9v.906a2.25 2.25 0 0 1-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 0 0 1.183 1.981l6.478 3.488m8.839 2.51-4.66-2.51m0 0-1.023-.55a2.25 2.25 0 0 0-2.134 0l-1.022.55m0 0-4.661 2.51m16.5 1.615a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V8.844a2.25 2.25 0 0 1 1.183-1.981l7.5-4.039a2.25 2.25 0 0 1 2.134 0l7.5 4.039a2.25 2.25 0 0 1 1.183 1.98V19.5Z"/>
                      </svg>
                    </div>
                    <p class="text-sm font-medium text-gray-500 dark:text-gray-400">No invitations found</p>
                    <p v-if="search" class="text-xs text-gray-400 dark:text-gray-500 mt-1">Try a different search</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="flex items-center justify-between mt-4 px-1">
          <span class="text-xs text-gray-400 dark:text-gray-500">
            Page {{ page.current_page }} of {{ page.total_pages }}
          </span>
          <div class="flex gap-1.5">
            <button @click="prevPage" :disabled="page.current_page <= 1"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-30 disabled:cursor-not-allowed"
              :class="page.current_page <= 1
                ? 'border-gray-200 dark:border-gray-700 text-gray-400'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              Prev
            </button>
            <button @click="nextPage" :disabled="page.current_page >= page.total_pages"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-30 disabled:cursor-not-allowed"
              :class="page.current_page >= page.total_pages
                ? 'border-gray-200 dark:border-gray-700 text-gray-400'
                : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'">
              Next
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Invitations</h1>
        <button @click="openModal" class="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">Invite</button>
      </div>
      <div class="px-4 pb-3">
        <div class="relative">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" placeholder="Search…"
            class="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
    </div>

    <div class="pb-24 pt-3 px-4 space-y-2">
      <div v-for="inv in invitations" :key="inv.id"
        class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 p-4">
        <div class="flex items-start gap-3">
          <img v-if="inv.joined_user?.profile_picture_url" :src="inv.joined_user.profile_picture_url" class="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
          <div v-else class="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5" :style="avatarStyle(inv.name)">{{ inv.avatar_name }}</div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <p class="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{{ inv.name }}</p>
              <span class="flex-shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5"
                :class="{
                  'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400': inv.status === 'pending',
                  'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400': inv.status === 'joined',
                  'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400': inv.status === 'declined',
                }">
                {{ inv.status.charAt(0).toUpperCase() + inv.status.slice(1) }}
              </span>
            </div>
            <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ inv.email }}</p>
            <div class="flex items-center justify-between mt-2">
              <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ formatDate(inv.created_at) }}</span>
              <button v-if="inv.status !== 'joined'" @click="reInvite(inv.id)" :disabled="reinviting === inv.id"
                class="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-40">
                {{ reinviting === inv.id ? 'Sending…' : 'Re-invite' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!invitations.length && !loading" class="flex flex-col items-center py-16 text-center">
        <p class="text-sm text-gray-400 dark:text-gray-500">No invitations found</p>
      </div>

      <div class="flex items-center justify-between pt-2">
        <span class="text-xs text-gray-400 dark:text-gray-500">Page {{ page.current_page }} of {{ page.total_pages }}</span>
        <div class="flex gap-2">
          <button @click="prevPage" :disabled="page.current_page <= 1" class="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30">Prev</button>
          <button @click="nextPage" :disabled="page.current_page >= page.total_pages" class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white disabled:opacity-30">Next</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Send Invite Modal -->
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-sm" @click="closeModal"></div>

        <!-- Panel -->
        <div class="relative w-full max-w-md bg-white dark:bg-[#1C2128] rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-10">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <div>
              <h2 class="text-base font-semibold text-gray-900 dark:text-white">Send Invitation</h2>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">The user will receive an email invitation to join.</p>
            </div>
            <button @click="closeModal" class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Form -->
          <form @submit.prevent="sendInvitation" class="px-6 py-5 space-y-4">
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Full name</label>
              <input v-model="form.name" type="text" required placeholder="Jane Smith"
                class="w-full px-3.5 py-2.5 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Email address</label>
              <input v-model="form.email" type="email" required placeholder="jane@example.com"
                class="w-full px-3.5 py-2.5 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
            </div>

            <div class="flex items-center gap-3 pt-1">
              <button type="submit" :disabled="sending"
                class="flex-1 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition">
                {{ sending ? 'Sending…' : 'Send invitation' }}
              </button>
              <button type="button" @click="closeModal"
                class="flex-1 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRequest } from 'composables/useRequest.js'
import { useAdminUserInvitationsStore } from 'stores/admin_user_invitations.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const store = useAdminUserInvitationsStore()

// Local UI-only state (not cached — modal/sending state doesn't need to survive navigation)
const showModal  = ref(false)
const sending    = ref(false)
const reinviting = ref(null)
const form       = ref({ name: '', email: '' })

// Derive everything the template needs from the store
const loading     = computed(() => store.loading)
const invitations = computed(() => store.records)
const page        = computed(() => store.page)

// Two-way bindings for controls — writes go through store setters that also invalidate the cache
const search = computed({
  get: () => store.search,
  set: (val) => store.setSearch(val),
})
const sortBy = computed({
  get: () => store.sortBy,
  set: (val) => store.setSort(val, store.sortOrder),
})
const sortOrder = computed(() => store.sortOrder)

// Avatar colours
const COLORS = [
  ['#dbeafe','#1d4ed8'],['#dcfce7','#15803d'],['#fef9c3','#a16207'],
  ['#fce7f3','#be185d'],['#ede9fe','#6d28d9'],['#ffedd5','#c2410c'],
]
function avatarStyle(name) {
  let h = 0
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  const [bg, color] = COLORS[Math.abs(h) % COLORS.length]
  return { background: bg, color }
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

let searchTimeout = null
function onSearchInput() {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => store.fetchList(), 300)
}

function toggleSortOrder() {
  store.setSort(store.sortBy, store.sortOrder === 'DESC' ? 'ASC' : 'DESC')
}

function nextPage() {
  if (store.page.current_page < store.page.total_pages) {
    store.goToPage(store.page.current_page + 1)
    store.fetchList()
  }
}
function prevPage() {
  if (store.page.current_page > 1) {
    store.goToPage(store.page.current_page - 1)
    store.fetchList()
  }
}

function openModal()  { showModal.value = true }
function closeModal() { showModal.value = false; form.value = { name: '', email: '' } }

async function sendInvitation() {
  sending.value = true
  try {
    const res = await axiosRequest('POST', '/api/v1/admin/user_invitations', { user_invitation: form.value }, false)
    if (!res.js_error && res.response.success) {
      flash.value?.show(res.response.message, 'notice')
      closeModal()
      store.invalidate()
      store.fetchList()
    } else {
      flash.value?.show(res.error?.data?.message ?? 'Something went wrong.', 'alert')
    }
  } finally {
    sending.value = false
  }
}

async function reInvite(id) {
  reinviting.value = id
  try {
    const res = await axiosRequest('POST', `/api/v1/admin/user_invitations/${id}/re_invite`, {}, false)
    if (!res.js_error && res.response.success) {
      flash.value?.show(res.response.message, 'notice')
      store.invalidate()
      store.fetchList()
    } else {
      flash.value?.show(res.error?.data?.message ?? 'Something went wrong.', 'alert')
    }
  } finally {
    reinviting.value = null
  }
}

// Re-fetch when sort changes (store setters already invalidate, so fetchList always runs)
watch([sortBy, sortOrder], () => store.fetchList())

onMounted(() => store.fetchIfStale())
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
